import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Archive, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Surface duplicate / un-verified-old partner_ledger_uploads so they can be
 * soft-archived. Archiving stops them from suppressing real pending
 * trackings on the Pending Sales tab and the Partner Ledger Template
 * export. Original docs remain intact (soft delete) and can be un-archived.
 */
const StaleUploadsCleanupDialog = ({ open, onOpenChange, token, isSuperAdmin, onArchived }) => {
  const [loading, setLoading] = useState(false);
  const [staleDays, setStaleDays] = useState(14);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [archiving, setArchiving] = useState(false);
  const [password, setPassword] = useState("");

  const runScan = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/uploads/stale-scan?stale_days=${encodeURIComponent(staleDays)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const t = await res.text();
        toast.error(`Scan failed: ${t.slice(0, 120)}`);
        setData(null);
        return;
      }
      setData(await res.json());
    } catch (e) {
      toast.error(`Scan error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Suggested defaults: every duplicate that ISN'T the recommended keeper,
  // plus every zero-verified-old row.
  const suggestSelection = () => {
    if (!data) return;
    const s = new Set();
    for (const g of data.duplicate_groups || []) {
      const keep = g.recommended_keep_upload_id;
      for (const u of g.uploads || []) {
        if (u.upload_id !== keep) s.add(u.upload_id);
      }
    }
    for (const u of data.zero_verified_old || []) s.add(u.upload_id);
    setSelected(s);
  };

  const clearSelection = () => setSelected(new Set());

  const toggle = (uploadId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uploadId)) next.delete(uploadId);
      else next.add(uploadId);
      return next;
    });
  };

  const selectedCount = selected.size;

  const doArchive = async () => {
    if (!isSuperAdmin) {
      toast.error("Only super_admin can archive uploads.");
      return;
    }
    if (!selectedCount) {
      toast.error("Select at least one upload to archive.");
      return;
    }
    if (!password) {
      toast.error("Enter your admin password to confirm.");
      return;
    }
    setArchiving(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/uploads/archive`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            upload_ids: Array.from(selected),
            password,
          }),
        }
      );
      const payload = await res.json();
      if (!res.ok) {
        toast.error(payload.detail || "Archive failed");
        return;
      }
      toast.success(
        `Archived ${payload.archived_count} / ${payload.requested_count} upload(s). Pending Sales will refresh.`
      );
      setPassword("");
      setSelected(new Set());
      // Re-scan to refresh data
      await runScan();
      onArchived?.();
    } catch (e) {
      toast.error(`Archive error: ${e.message}`);
    } finally {
      setArchiving(false);
    }
  };

  const fmtDT = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const totalCandidates = useMemo(() => {
    if (!data) return 0;
    const ids = new Set();
    for (const g of data.duplicate_groups || []) {
      for (const u of g.uploads || []) ids.add(u.upload_id);
    }
    for (const u of data.zero_verified_old || []) ids.add(u.upload_id);
    return ids.size;
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="stale-uploads-cleanup-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
            Cleanup Stale Partner-Ledger Uploads
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Surfaces (a) duplicate filename per partner and (b) uploads older
            than N days with <b>zero verified entries</b>. Archiving (soft
            delete) removes them from the Pending Sales calculation so they
            stop hiding real pending trackings. Originals remain intact and
            can be un-archived later.
          </DialogDescription>
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 border-b pb-3">
          <div>
            <Label className="text-[11px] text-slate-600">Stale window (days)</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={staleDays}
              onChange={(e) => setStaleDays(Number(e.target.value) || 14)}
              className="w-28 h-8 text-sm"
              data-testid="stale-days-input"
            />
          </div>
          <Button
            size="sm"
            onClick={runScan}
            disabled={loading}
            data-testid="stale-scan-run-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Re-scan
          </Button>
          {data && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={suggestSelection}
                disabled={loading || totalCandidates === 0}
                data-testid="stale-suggest-selection-btn"
              >
                Suggest selection
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                disabled={selectedCount === 0}
              >
                Clear ({selectedCount})
              </Button>
              <div className="text-[11px] text-slate-500 ml-auto">
                Scanned <b>{data.scanned_uploads}</b> uploads ·{" "}
                <b>{data.duplicate_groups_count}</b> duplicate groups ·{" "}
                <b>{data.zero_verified_old_count}</b> stale rows
              </div>
            </>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : !data ? (
            <div className="text-center text-slate-500 py-12 text-sm">
              No scan data yet. Click <b>Re-scan</b>.
            </div>
          ) : data.duplicate_groups_count === 0 &&
            data.zero_verified_old_count === 0 ? (
            <div className="text-center py-12 text-emerald-600 bg-emerald-50 rounded-lg my-4">
              <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
              <p className="text-sm font-medium">
                All clean — no duplicate filenames or stale zero-verified
                uploads found.
              </p>
            </div>
          ) : (
            <div className="space-y-6 py-3">
              {/* Duplicate groups */}
              {data.duplicate_groups?.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Duplicate filename per partner ({data.duplicate_groups.length})
                  </h3>
                  <div className="space-y-3">
                    {data.duplicate_groups.map((g) => (
                      <div
                        key={`${g.partner_code}-${g.filename}`}
                        className="border border-amber-200 bg-amber-50/40 rounded-lg p-3"
                        data-testid={`dup-group-${g.partner_code}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                            {g.partner_code}
                          </Badge>
                          <span className="font-mono text-xs text-slate-700 truncate">
                            {g.filename}
                          </span>
                          <Badge variant="outline" className="text-[10px]">
                            × {g.count} copies
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          {g.uploads.map((u) => {
                            const isKeep =
                              u.upload_id === g.recommended_keep_upload_id;
                            return (
                              <label
                                key={u.upload_id}
                                className={`flex items-center gap-3 text-xs px-2 py-1.5 rounded ${
                                  selected.has(u.upload_id)
                                    ? "bg-rose-100"
                                    : "hover:bg-white"
                                }`}
                              >
                                <Checkbox
                                  checked={selected.has(u.upload_id)}
                                  onCheckedChange={() => toggle(u.upload_id)}
                                  data-testid={`dup-row-${u.upload_id}`}
                                />
                                {isKeep ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                                    Keep
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] text-slate-500"
                                  >
                                    Older
                                  </Badge>
                                )}
                                <span className="text-slate-500">
                                  {fmtDT(u.uploaded_at)}
                                </span>
                                <span className="text-slate-400">·</span>
                                <span>
                                  by <b>{u.uploaded_by || "?"}</b>
                                </span>
                                <span className="text-slate-400">·</span>
                                <span
                                  className={
                                    u.verified_entries === 0
                                      ? "text-rose-600"
                                      : ""
                                  }
                                >
                                  verified {u.verified_entries}/
                                  {u.total_entries}
                                </span>
                                <span className="text-slate-400">·</span>
                                <span className="text-slate-400 font-mono truncate flex-1">
                                  {u.upload_id}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Zero verified old */}
              {data.zero_verified_old?.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-500" />
                    Zero verified entries, older than {data.stale_days} days (
                    {data.zero_verified_old.length})
                  </h3>
                  <div className="space-y-1">
                    {data.zero_verified_old.map((u) => (
                      <label
                        key={u.upload_id}
                        className={`flex items-center gap-3 text-xs px-2 py-1.5 rounded border ${
                          selected.has(u.upload_id)
                            ? "bg-rose-100 border-rose-300"
                            : "border-slate-200 hover:bg-white"
                        }`}
                      >
                        <Checkbox
                          checked={selected.has(u.upload_id)}
                          onCheckedChange={() => toggle(u.upload_id)}
                          data-testid={`stale-row-${u.upload_id}`}
                        />
                        <Badge className="bg-slate-100 text-slate-700 border-slate-300">
                          {(u.partner_codes || []).join(", ") || "?"}
                        </Badge>
                        <span className="font-mono truncate flex-1">
                          {u.filename}
                        </span>
                        <span className="text-slate-500">
                          {fmtDT(u.uploaded_at)}
                        </span>
                        <span className="text-rose-600">
                          {u.verified_entries}/{u.total_entries} verified
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer: archive controls */}
        <DialogFooter className="border-t pt-3 flex-row items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[260px]">
            <Label
              className="text-[11px] text-slate-500 whitespace-nowrap"
              htmlFor="archive-pw"
            >
              Admin password
            </Label>
            <Input
              id="archive-pw"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Required to archive"
              className="w-48 h-8 text-sm"
              data-testid="stale-archive-password"
            />
            <span className="text-[11px] text-slate-500">
              {selectedCount} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={doArchive}
              disabled={
                archiving ||
                !isSuperAdmin ||
                selectedCount === 0 ||
                !password
              }
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="stale-archive-confirm-btn"
            >
              {archiving ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Archive className="w-3.5 h-3.5 mr-1" />
              )}
              Archive {selectedCount > 0 ? `(${selectedCount})` : ""}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StaleUploadsCleanupDialog;
